import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../../components/navigation/Sidebar';
import NotificationCenter from '../../components/navigation/NotificationCenter';
import QuickActionMenu from '../../components/navigation/QuickActionMenu';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { getCreditBreakdown } from '../../services/creditService';
import { getUserSubscription } from '../../services/caseService';
import { createSubscriptionCheckout, createOneTimeCaseCheckout } from '../../services/stripeCheckoutService';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

// ─── Plan display helpers ────────────────────────────────────────────────────
// DB enum is: free_trial | single_case | monthly | annual | enterprise
// We derive a more specific "effective plan" using plan_type + monthly_credits.
function deriveEffectivePlan(sub) {
  const pt = sub?.plan_type || 'free_trial';
  if (pt === 'free_trial' || pt === 'enterprise') return pt;
  const credits = sub?.monthly_credits ?? 0;
  // Starter = 3 credits, Professional = 10 credits
  const tier = credits >= 10 ? 'professional' : 'starter';
  if (pt === 'monthly') return `${tier}_monthly`;
  if (pt === 'annual') return `${tier}_annual`;
  return pt; // single_case / anything else
}

const PLAN_LABELS = {
  free_trial: 'Free Trial',
  single_case: 'Single Case',
  starter_monthly: 'Starter — Monthly',
  starter_annual: 'Starter — Annual',
  professional_monthly: 'Professional — Monthly',
  professional_annual: 'Professional — Annual',
  enterprise: 'Enterprise',
};

const PLAN_COLORS = {
  free_trial: 'bg-muted text-muted-foreground border-border',
  single_case: 'bg-muted text-muted-foreground border-border',
  starter_monthly: 'bg-primary/10 text-primary border-primary/30',
  starter_annual: 'bg-primary/10 text-primary border-primary/30',
  professional_monthly: 'bg-success/10 text-success border-success/30',
  professional_annual: 'bg-success/10 text-success border-success/30',
  enterprise: 'bg-warning/10 text-warning border-warning/30',
};

// ─── Payment-method card helpers ─────────────────────────────────────────────
const CARD_ICONS = {
  visa: 'CreditCard',
  mastercard: 'CreditCard',
  amex: 'CreditCard',
  discover: 'CreditCard',
};

const ClientFinancial = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, signOut } = useAuth();

  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'overview');
  const [creditBreakdown, setCreditBreakdown] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPayment, setIsLoadingPayment] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [buyQty, setBuyQty] = useState(1);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [allTransactions, setAllTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t) setActiveTab(t);
  }, [searchParams]);

  // Auto-open buy credits modal or payment method tab when redirected from New Case
  useEffect(() => {
    const action = searchParams.get('action');
    const reason = searchParams.get('reason');
    if (action === 'buy-credits') {
      setBuyQty(1);
      setShowBuyModal(true);
      // Clean URL without triggering re-render loop
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('action');
        return next;
      }, { replace: true });
      toast('You need credits to create a case. Purchase credits below.', { icon: '💳' });
    }
    if (reason === 'no-payment-method') {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('reason');
        return next;
      }, { replace: true });
      toast('Please add a payment method before creating a case.', { icon: '💳' });
    }
  }, [searchParams]);

  useEffect(() => {
    if (user) {
      // Detect return from Stripe checkout
      const checkoutStatus = searchParams.get('checkout');
      if (checkoutStatus === 'success') {
        // Clean the URL
        setSearchParams({ tab: 'overview' }, { replace: true });
        toast.success('Payment successful! Your credits will appear shortly.');
        // Wait briefly for webhook to process, then refresh
        const timer = setTimeout(() => loadData(), 4000);
        loadData();
        return () => clearTimeout(timer);
      } else if (checkoutStatus === 'canceled') {
        setSearchParams({ tab: 'overview' }, { replace: true });
        toast.error('Checkout canceled.');
        loadData();
      } else {
        loadData();
      }
    }
  }, [user?.id]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [breakdownResult, subscriptionResult] = await Promise.all([
        getCreditBreakdown(user?.id),
        getUserSubscription(user?.id),
      ]);
      if (breakdownResult?.success) setCreditBreakdown(breakdownResult.breakdown);
      if (subscriptionResult?.success) setSubscription(subscriptionResult.data);
    } catch (err) {
      console.error('Load financial data error:', err);
      toast.error('Failed to load financial data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAllTransactions = async () => {
    if (!user?.id) return;
    try {
      setLoadingTransactions(true);

      // Fetch payments + tips in parallel
      const [paymentsResult, { data: tipRows }] = await Promise.all([
        supabase
          .from('payments')
          .select('id, amount, refunded_amount, payment_type, payment_status, description, created_at')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false }),
        // RLS filters to contracts owned by this client
        supabase
          .from('tips')
          .select('id, amount, created_at, contract:marketplace_contracts(title)')
          .eq('status', 'succeeded')
          .order('created_at', { ascending: false }),
      ]);

      // If refunded_amount column doesn't exist yet, fall back without it
      let rows = paymentsResult.data;
      if (paymentsResult.error) {
        const { data: fallbackRows } = await supabase
          .from('payments')
          .select('id, amount, payment_type, payment_status, description, created_at')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
        rows = (fallbackRows || []).map(r => ({ ...r, refunded_amount: 0 }));
      }

      const mapped = (rows || []).flatMap((p) => {
        const isEscrow    = p.payment_type === 'escrow';
        const isRefunded  = p.payment_status === 'refunded';           // full refund
        const partialAmt  = parseFloat(p.refunded_amount || 0);
        const hasPartial  = !isRefunded && partialAmt > 0;             // partial refund
        const effectiveCharge = isRefunded ? p.amount : p.amount - partialAmt;

        // ── Non-escrow rows (subscriptions, credit purchases) ───────────
        if (!isEscrow) {
          const category = p.payment_type === 'subscription' ? 'Subscription' : 'Credit Purchase';
          return [{
            id: `pay-${p.id}`,
            date: p.created_at,
            category,
            description: p.description || category,
            amount: p.amount,
            flow: isRefunded ? 'refund' : 'debit',
            status: p.payment_status,
            icon: p.payment_type === 'subscription' ? 'RefreshCw' : 'Zap',
            iconBg: 'bg-primary/10',
            iconColor: 'text-primary',
          }];
        }

        // ── Escrow rows ─────────────────────────────────────────────────
        const chargeRow = {
          id: `pay-${p.id}-charge`,
          date: p.created_at,
          category: 'Escrow Payment',
          description: p.description || 'Escrow Payment',
          // Show effective amount (full minus any partial refund) as the charge
          amount: effectiveCharge,
          flow: 'debit',
          status: isRefunded ? 'voided' : p.payment_status,
          icon: isRefunded ? 'RotateCcw' : p.payment_status === 'succeeded' ? 'CheckCircle' : 'Lock',
          iconBg: isRefunded ? 'bg-muted' : p.payment_status === 'succeeded' ? 'bg-success/10' : 'bg-warning/10',
          iconColor: isRefunded ? 'text-muted-foreground' : p.payment_status === 'succeeded' ? 'text-success' : 'text-warning',
        };

        const contractLabel = (p.description || '').replace(/^Escrow:?\s*/i, '').trim() || 'Escrow Payment';

        // Full refund → voided charge + full refund row
        if (isRefunded) {
          return [chargeRow, {
            id: `pay-${p.id}-refund`,
            date: p.created_at,
            category: 'Escrow Refund',
            description: `Refund: ${contractLabel}`,
            amount: p.amount,
            flow: 'refund',
            status: 'refunded',
            icon: 'RotateCcw',
            iconBg: 'bg-success/10',
            iconColor: 'text-success',
          }];
        }

        // Partial refund → charge row (already shows net amount) + partial refund row
        if (hasPartial) {
          return [chargeRow, {
            id: `pay-${p.id}-partial-refund`,
            date: p.created_at,
            category: 'Partial Refund',
            description: `Partial refund: ${contractLabel}`,
            amount: partialAmt,
            flow: 'refund',
            status: 'refunded',
            icon: 'RotateCcw',
            iconBg: 'bg-success/10',
            iconColor: 'text-success',
          }];
        }

        return [chargeRow];
      });

      // Merge tip rows
      const tipMapped = (tipRows || []).map((t) => ({
        id: `tip-${t.id}`,
        date: t.created_at,
        category: 'Tip',
        description: `Tip: ${t.contract?.title || 'Contract'}`,
        amount: t.amount,
        flow: 'debit',
        status: 'succeeded',
        icon: 'Heart',
        iconBg: 'bg-amber-500/10',
        iconColor: 'text-amber-500',
      }));

      // Sort all combined rows newest-first
      const combined = [...mapped, ...tipMapped].sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );

      setAllTransactions(combined);
    } catch (err) {
      console.error('Load transactions error:', err);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleBuyCredits = async (planId) => {
    if (planId === 'single_case') {
      // "Buy Credits" button → show modal so user can pick quantity
      setBuyQty(1);
      setShowBuyModal(true);
      return;
    }
    if (planId === 'single_case_direct') {
      // Single Case plan card → skip modal, go straight to checkout for 1 credit
      try {
        setIsLoadingPayment(true);
        const result = await createOneTimeCaseCheckout(user?.id, user?.email, 1);
        if (result?.isTestAccount) {
          toast.success('Test account — no payment required.');
          return;
        }
        if (result?.success && result?.sessionUrl) {
          window.location.href = result.sessionUrl;
        } else {
          toast.error(result?.error || 'Failed to start checkout');
        }
      } catch {
        toast.error('Checkout failed');
      } finally {
        setIsLoadingPayment(false);
      }
      return;
    }
    try {
      setIsLoadingPayment(true);
      const result = await createSubscriptionCheckout(planId, user?.id, user?.email);
      if (result?.isTestAccount) {
        toast.success('Test account — no payment required.');
        return;
      }
      if (result?.success && result?.sessionUrl) {
        window.location.href = result.sessionUrl;
      } else {
        toast.error(result?.error || 'Failed to start checkout');
      }
    } catch {
      toast.error('Checkout failed');
    } finally {
      setIsLoadingPayment(false);
    }
  };

  // Plan rank — used to label "Upgrade" vs "Downgrade"
  const PLAN_RANK = {
    free_trial: 0,
    starter_monthly: 2,
    starter_annual: 2,
    professional_monthly: 3,
    professional_annual: 3,
    enterprise: 4,
  };

  /**
   * handleUpgradeSubscription — in-place Stripe subscription update with proration.
   * Stripe automatically credits the unused portion of the old plan and charges the
   * prorated difference for the new plan. No checkout redirect needed.
   */
  const handleUpgradeSubscription = async (newPlanKey) => {
    const planLabel = PLAN_LABELS[newPlanKey] || newPlanKey;
    const confirmed = window.confirm(
      `Upgrade to ${planLabel}?\n\nYou'll only be charged the prorated difference — unused time on your current plan is credited automatically.`
    );
    if (!confirmed) return;

    try {
      setIsLoadingPayment(true);
      const { data, error } = await supabase.functions.invoke('upgrade-subscription', {
        body: { userId: user?.id, newPlanKey },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.requiresPayment && data?.paymentUrl) {
        // No saved card — redirect user to Stripe's hosted invoice page to pay
        window.location.href = data.paymentUrl;
        return;
      }

      // Payment was collected automatically from saved card
      toast.success(`Upgraded to ${planLabel}! $${data?.amountCharged?.toFixed(2)} charged (prorated).`);
      loadData();
    } catch (err) {
      console.error('Upgrade error:', err);
      toast.error(err.message || 'Upgrade failed');
    } finally {
      setIsLoadingPayment(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setIsLoadingPayment(true);
      const { data, error } = await supabase.functions.invoke('create-billing-portal', {
        body: {
          userId: user?.id,
          returnUrl: `${window.location.origin}/client-dashboard/financial?tab=overview`,
        },
      });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not open billing portal');
      }
    } catch (err) {
      console.error('Manage subscription error:', err);
      toast.error('Failed to open billing portal');
    } finally {
      setIsLoadingPayment(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm('Cancel your subscription? You\'ll keep access until the end of the current billing period.')) return;
    try {
      setIsLoadingPayment(true);
      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        body: { userId: user?.id, cancelAtPeriodEnd: true },
      });
      if (error) throw error;
      toast.success('Subscription will cancel at end of billing period.');
      loadData();
    } catch (err) {
      console.error('Cancel subscription error:', err);
      toast.error('Failed to cancel subscription');
    } finally {
      setIsLoadingPayment(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setIsLoadingPayment(true);
      const { data, error } = await supabase.functions.invoke('reactivate-subscription', {
        body: { userId: user?.id },
      });
      if (error) throw error;
      toast.success('Subscription reactivated!');
      loadData();
    } catch (err) {
      console.error('Reactivate subscription error:', err);
      toast.error('Failed to reactivate subscription');
    } finally {
      setIsLoadingPayment(false);
    }
  };

  const handleConfirmBuyCredits = async () => {
    try {
      setIsLoadingPayment(true);
      setShowBuyModal(false);
      const result = await createOneTimeCaseCheckout(user?.id, user?.email, buyQty);
      if (result?.isTestAccount) {
        toast.success('Test account — no payment required.');
        return;
      }
      if (result?.success && result?.sessionUrl) {
        window.location.href = result.sessionUrl;
      } else {
        toast.error(result?.error || 'Failed to start checkout');
      }
    } catch {
      toast.error('Checkout failed');
    } finally {
      setIsLoadingPayment(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'transactions' && user?.id) loadAllTransactions();
  }, [activeTab, user?.id]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'transactions', label: 'Transactions', icon: 'Receipt' },
    { id: 'payment-method', label: 'Payment Method', icon: 'CreditCard' },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Icon name="Loader" size={48} className="text-primary animate-spin" />
      </div>
    );
  }

  const planKey = deriveEffectivePlan(subscription);

  return (
    <div className="min-h-screen bg-background">
      <Sidebar userRole="client" />
      <QuickActionMenu userRole="client" />
      <div className="lg:ml-60 min-h-screen">
        {/* ── Header ── */}
        <header className="sticky top-0 z-40 bg-card border-b border-border px-4 md:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  const c = document.body.classList.toggle('sidebar-collapsed');
                  localStorage.setItem('sidebarCollapsed', c.toString());
                }}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
                aria-label="Toggle sidebar"
              >
                <Icon name="PanelLeft" size={24} />
              </button>
              <div>
                <h1 className="text-2xl md:text-3xl font-semibold text-foreground">Financial</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Credits, billing history &amp; payment methods.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <NotificationCenter userRole="client" />
              <Button
                variant="outline"
                iconName="LogOut"
                iconPosition="left"
                onClick={async () => {
                  await signOut();
                  navigate('/login');
                }}
              >
                Logout
              </Button>
            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6 md:py-8 space-y-6">
          {/* ── Tab bar ── */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="border-b border-border">
              <div className="flex">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-smooth border-b-2 ${
                      activeTab === tab.id
                        ? 'border-primary text-primary bg-primary/5'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    <Icon name={tab.icon} size={15} />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-6">

              {/* ════════════ OVERVIEW ════════════ */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Credit balance grid */}
                  <div>
                    <h2 className="text-base font-semibold text-foreground mb-3">Credit Balance</h2>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      {[
                        {
                          label: 'Total Credits',
                          value: creditBreakdown?.total ?? 0,
                          icon: 'Zap',
                          color: 'text-primary',
                          bg: 'bg-primary/10',
                          desc: 'Available to use',
                        },
                        {
                          label: 'Trial Credits',
                          value: creditBreakdown?.trial ?? 0,
                          icon: 'Gift',
                          color: 'text-info',
                          bg: 'bg-info/10',
                          desc: 'One-time complimentary',
                        },
                        {
                          label: 'Monthly Credits',
                          value: creditBreakdown?.monthly ?? 0,
                          icon: 'RefreshCw',
                          color: 'text-success',
                          bg: 'bg-success/10',
                          desc: 'Renew with subscription',
                        },
                        {
                          label: 'Paid Credits',
                          value: creditBreakdown?.paid ?? 0,
                          icon: 'ShoppingCart',
                          color: 'text-warning',
                          bg: 'bg-warning/10',
                          desc: 'Purchased, never expire',
                        },
                      ].map((card) => (
                        <div
                          key={card.label}
                          className="bg-background border border-border rounded-xl p-4"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-sm text-muted-foreground font-medium">{card.label}</p>
                            <div
                              className={`w-8 h-8 rounded-lg ${card.bg} flex items-center justify-center`}
                            >
                              <Icon name={card.icon} size={15} className={card.color} />
                            </div>
                          </div>
                          <p className={`text-3xl font-bold ${card.color}`}>{card.value}</p>
                          <p className="text-xs text-muted-foreground mt-1">{card.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Subscription plan card */}
                  <div>
                    <h2 className="text-base font-semibold text-foreground mb-3">Current Plan</h2>
                    <div className={`bg-background border rounded-xl p-5 ${subscription?.cancel_at_period_end ? 'border-warning/50' : 'border-border'}`}>
                      {/* Pending cancellation banner — only when sub is still alive */}
                      {subscription?.cancel_at_period_end && subscription?.is_active && (
                        <div className="flex items-center gap-3 bg-warning/10 border border-warning/20 rounded-lg px-4 py-3 mb-4">
                          <Icon name="AlertTriangle" size={18} className="text-warning flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-foreground">Subscription cancelling</p>
                            <p className="text-xs text-muted-foreground">
                              You'll keep full access until{' '}
                              <span className="font-medium text-foreground">
                                {new Date(subscription.cancels_at || subscription.billing_period_end).toLocaleDateString(
                                  'en-US', { month: 'long', day: 'numeric', year: 'numeric' }
                                )}
                              </span>. After that your plan reverts to Free Trial.
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleReactivateSubscription}
                            disabled={isLoadingPayment}
                          >
                            {isLoadingPayment ? 'Loading…' : 'Reactivate'}
                          </Button>
                        </div>
                      )}
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span
                              className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${PLAN_COLORS[planKey] || PLAN_COLORS.free_trial}`}
                            >
                              {PLAN_LABELS[planKey] || planKey}
                            </span>
                            {subscription?.status && (
                              <span
                                className={`px-2 py-0.5 text-xs rounded-full ${
                                  subscription.status === 'active'
                                    ? 'bg-success/10 text-success'
                                    : 'bg-muted text-muted-foreground'
                                }`}
                              >
                                {subscription.cancel_at_period_end ? 'cancelling' : subscription.status}
                              </span>
                            )}
                          </div>
                          {subscription?.billing_period_end && planKey !== 'free_trial' && (
                            <p className="text-sm text-muted-foreground">
                              {subscription.cancel_at_period_end ? (
                                <span className="text-warning font-medium">
                                  Expires{' '}
                                  {new Date(subscription.cancels_at || subscription.billing_period_end).toLocaleDateString(
                                    'en-US',
                                    { month: 'long', day: 'numeric', year: 'numeric' },
                                  )}
                                </span>
                              ) : subscription.is_active ? (
                                <>
                                  Renews{' '}
                                  {new Date(subscription.billing_period_end).toLocaleDateString(
                                    'en-US',
                                    { month: 'long', day: 'numeric', year: 'numeric' },
                                  )}
                                </>
                              ) : null}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-4 pt-1 text-sm text-muted-foreground">
                            {subscription?.trial_credits != null && (
                              <span className="flex items-center gap-1">
                                <Icon name="Gift" size={13} className="text-info" />
                                {subscription.trial_credits} trial
                              </span>
                            )}
                            {subscription?.monthly_credits != null && (
                              <span className="flex items-center gap-1">
                                <Icon name="RefreshCw" size={13} className="text-success" />
                                {subscription.monthly_credits} monthly
                              </span>
                            )}
                            {subscription?.paid_credits != null && (
                              <span className="flex items-center gap-1">
                                <Icon name="ShoppingCart" size={13} className="text-warning" />
                                {subscription.paid_credits} paid
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant="default"
                            size="sm"
                            iconName="Plus"
                            iconPosition="left"
                            onClick={() => handleBuyCredits('single_case')}
                            disabled={isLoadingPayment}
                          >
                            Buy Credits
                          </Button>
                          {subscription?.is_active && planKey !== 'free_trial' && !subscription?.cancel_at_period_end && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                iconName="Settings"
                                iconPosition="left"
                                onClick={handleManageSubscription}
                                disabled={isLoadingPayment}
                              >
                                {isLoadingPayment ? 'Loading…' : 'Manage Subscription'}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                iconName="XCircle"
                                iconPosition="left"
                                onClick={handleCancelSubscription}
                                disabled={isLoadingPayment}
                                className="text-destructive hover:text-destructive"
                              >
                                Cancel
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Plans — always visible so active subscribers can see upgrade / downgrade options */}
                  {(() => {
                    const pricingPlans = [
                      {
                        id: 'single',
                        planId: 'single_case',
                        name: 'Single Case',
                        description: 'Perfect for one-time analysis needs',
                        price: { monthly: 99, annual: 99 },
                        features: [
                          '1 case credit',
                          'AI-powered preliminary report',
                          '15-section comprehensive analysis',
                          'Image annotation engine',
                          'PDF report download',
                          'Expert marketplace access',
                        ],
                        popular: false,
                        cta: 'Get Started',
                      },
                      {
                        id: 'starter',
                        planId: billingCycle === 'monthly' ? 'starter_monthly' : 'starter_annual',
                        name: 'Starter',
                        description: 'For regular case analysis',
                        price: { monthly: 250, annual: 199 },
                        features: [
                          '3 cases per month',
                          'All Single Case features',
                          'Priority AI processing',
                          'Email Support',
                        ],
                        popular: true,
                        cta: 'Start Free Trial',
                      },
                      {
                        id: 'professional',
                        planId: billingCycle === 'monthly' ? 'professional_monthly' : 'professional_annual',
                        name: 'Professional',
                        description: 'For high-volume users',
                        price: { monthly: 500, annual: 399 },
                        features: [
                          '10 cases per month',
                          'All Starter features',
                          'Dedicated account manager',
                        ],
                        popular: false,
                        cta: 'Start Free Trial',
                      },
                      {
                        id: 'enterprise',
                        planId: 'enterprise',
                        name: 'Enterprise',
                        description: 'For organizations with custom needs',
                        price: { monthly: 'Custom', annual: 'Custom' },
                        features: [
                          'Unlimited cases',
                          'All Professional features',
                          'Custom training sessions',
                        ],
                        popular: false,
                        cta: 'Contact Sales',
                      },
                    ];
                    return (
                      <div>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
                          <h2 className="text-base font-semibold text-foreground">Plans</h2>
                          <div className="inline-flex items-center gap-1 p-1 bg-card border border-border rounded-lg self-start">
                            <button
                              className={`px-4 py-1.5 rounded-md transition-smooth text-sm ${
                                billingCycle === 'monthly'
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-foreground hover:bg-muted'
                              }`}
                              onClick={() => setBillingCycle('monthly')}
                            >
                              Monthly
                            </button>
                            <button
                              className={`px-4 py-1.5 rounded-md transition-smooth text-sm ${
                                billingCycle === 'annual'
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-foreground hover:bg-muted'
                              }`}
                              onClick={() => setBillingCycle('annual')}
                            >
                              Annual
                              <span className="ml-1.5 px-1.5 py-0.5 bg-success text-success-foreground rounded text-xs">
                                Save 20%
                              </span>
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                          {pricingPlans.map((plan) => {
                            // Detect if this card matches the user's active plan
                            const isPlanCurrent =
                              planKey !== 'free_trial' &&
                              !subscription?.cancel_at_period_end &&
                              (plan.planId === planKey ||
                                (plan.id === 'starter' && (planKey === 'starter_monthly' || planKey === 'starter_annual')) ||
                                (plan.id === 'professional' && (planKey === 'professional_monthly' || planKey === 'professional_annual')));
                            const hasActiveSub = subscription?.is_active && planKey !== 'free_trial' && !subscription?.cancel_at_period_end;
                            const currentRank = PLAN_RANK[planKey] ?? 0;
                            // PLAN_RANK has no entry for 'single_case' → cardRank = 0 (below any paid plan)
                            const cardRank = PLAN_RANK[plan.planId] ?? PLAN_RANK[`${plan.id}_monthly`] ?? 0;
                            // Disable cards at or below the user's current plan tier.
                            // If the subscription is pending cancellation, allow all plans so the user
                            // can re-subscribe to the same or a different plan.
                            const isDisabledPlan = planKey !== 'free_trial'
                              && !subscription?.cancel_at_period_end
                              && cardRank <= currentRank;
                            const ctaLabel = isPlanCurrent
                              ? 'Current Plan'
                              : hasActiveSub && cardRank > currentRank && plan.id !== 'enterprise'
                              ? 'Upgrade'
                              : plan.cta;
                            return (
                            <div
                              key={plan.id}
                              className={`relative bg-card border rounded-xl p-6 flex flex-col transition-smooth hover:shadow-elevation-lg ${
                                isPlanCurrent
                                  ? 'border-success ring-1 ring-success/40'
                                  : plan.popular
                                  ? 'border-primary shadow-elevation-md'
                                  : 'border-border'
                              }`}
                            >
                              {isPlanCurrent && (
                                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-success text-success-foreground rounded-full text-xs font-medium whitespace-nowrap">
                                    <Icon name="CheckCircle" size={12} />
                                    Current Plan
                                  </span>
                                </div>
                              )}
                              {!isPlanCurrent && plan.popular && (
                                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary text-primary-foreground rounded-full text-xs font-medium whitespace-nowrap">
                                    <Icon name="Star" size={12} />
                                    Most Popular
                                  </span>
                                </div>
                              )}

                              <div className="mb-4">
                                <h3 className="text-lg font-bold text-foreground mb-1">{plan.name}</h3>
                                <p className="text-xs text-muted-foreground">{plan.description}</p>
                              </div>

                              <div className="mb-5">
                                {typeof plan.price[billingCycle] === 'number' ? (
                                  <>
                                    {/* Show struck-through monthly price when annual discount applies */}
                                    {billingCycle === 'annual' && plan.price.monthly !== plan.price.annual && (
                                      <div className="flex items-baseline gap-1.5 mb-0.5">
                                        <span className="text-sm line-through text-muted-foreground">
                                          ${plan.price.monthly}
                                        </span>
                                        <span className="text-xs font-medium text-success">Save 20%</span>
                                      </div>
                                    )}
                                    <div className="flex items-baseline gap-1">
                                      <span className="text-3xl font-bold text-foreground">
                                        ${plan.price[billingCycle]}
                                      </span>
                                      <span className="text-sm text-muted-foreground">/mo</span>
                                    </div>
                                    {billingCycle === 'annual' && plan.price.monthly !== plan.price.annual && (
                                      <p className="text-xs text-muted-foreground mt-0.5">
                                        billed annually (${plan.price.annual * 12}/yr)
                                      </p>
                                    )}
                                  </>
                                ) : (
                                  <div className="flex items-baseline gap-1">
                                    <span className="text-3xl font-bold text-foreground">
                                      {plan.price[billingCycle]}
                                    </span>
                                  </div>
                                )}
                              </div>

                              <Button
                                variant={isDisabledPlan ? 'ghost' : plan.popular ? 'default' : 'outline'}
                                size="sm"
                                fullWidth
                                className="mb-5"
                                disabled={isLoadingPayment || isDisabledPlan}
                                onClick={() => {
                                  if (isDisabledPlan) return;
                                  if (plan.id === 'enterprise') {
                                    navigate('/enterprise-sales');
                                  } else if (plan.id === 'single') {
                                    handleBuyCredits('single_case_direct');
                                  } else if (hasActiveSub && cardRank > currentRank) {
                                    // User has an active subscription and is upgrading to a higher tier.
                                    // Use in-place subscription update with proration — no checkout redirect.
                                    handleUpgradeSubscription(plan.planId);
                                  } else {
                                    // New subscription (free-trial users, pending-cancel users re-subscribing)
                                    handleBuyCredits(plan.planId);
                                  }
                                }}
                              >
                                {isLoadingPayment ? 'Loading…' : ctaLabel}
                              </Button>

                              <div className="space-y-2.5 mt-auto">
                                {plan.features.map((feature, i) => (
                                  <div key={i} className="flex items-start gap-2">
                                    <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" />
                                    <span className="text-xs text-foreground">{feature}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            );
                          })}
                        </div>

                        {/* Enterprise CTA */}
                        <div className="mt-8 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg p-6 text-center border border-border">
                          <Icon name="Briefcase" size={36} className="mx-auto mb-3 text-primary" />
                          <h3 className="text-lg font-bold text-foreground mb-1">Enterprise Solutions</h3>
                          <p className="text-sm text-muted-foreground mb-4 max-w-xl mx-auto">
                            Need high-volume case processing? Get custom pricing, dedicated support, and advanced features tailored to your organization.
                          </p>
                          <button
                            onClick={() => navigate('/enterprise-sales')}
                            className="px-6 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth inline-flex items-center gap-2 text-sm"
                          >
                            <Icon name="Mail" size={16} />
                            Contact Sales Team
                          </button>
                        </div>

                        <p className="text-xs text-muted-foreground text-center mt-4">
                          All plans include 256-bit encryption, 2FA security, and methodology aligned with NFPA 921
                        </p>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* ════════════ BUY CREDITS MODAL ════════════ */}
              {showBuyModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
                  {/* Backdrop */}
                  <div
                    className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                    onClick={() => setShowBuyModal(false)}
                  />
                  {/* Panel */}
                  <div className="relative z-10 w-full max-w-sm bg-card border border-border rounded-2xl shadow-xl p-6 flex flex-col gap-5">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">Buy Credits</h2>
                        <p className="text-sm text-muted-foreground mt-0.5">$99 per credit &mdash; use one per case</p>
                      </div>
                      <button
                        onClick={() => setShowBuyModal(false)}
                        className="p-1.5 hover:bg-muted rounded-lg transition-colors text-muted-foreground"
                        aria-label="Close"
                      >
                        <Icon name="X" size={18} />
                      </button>
                    </div>

                    {/* Quick-select pills */}
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Quick select</p>
                      <div className="flex gap-2 flex-wrap">
                        {[1, 3, 5, 10].map((n) => (
                          <button
                            key={n}
                            onClick={() => setBuyQty(n)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                              buyQty === n
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-background text-foreground border-border hover:border-primary/60'
                            }`}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Stepper */}
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Quantity</p>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setBuyQty((q) => Math.max(1, q - 1))}
                          className="w-9 h-9 rounded-lg border border-border flex items-center justify-center hover:bg-muted transition-colors disabled:opacity-40"
                          disabled={buyQty <= 1}
                          aria-label="Decrease"
                        >
                          <Icon name="Minus" size={16} />
                        </button>
                        <span className="w-10 text-center text-xl font-bold text-foreground tabular-nums">{buyQty}</span>
                        <button
                          onClick={() => setBuyQty((q) => Math.min(20, q + 1))}
                          className="w-9 h-9 rounded-lg border border-border flex items-center justify-center hover:bg-muted transition-colors disabled:opacity-40"
                          disabled={buyQty >= 20}
                          aria-label="Increase"
                        >
                          <Icon name="Plus" size={16} />
                        </button>
                        <span className="ml-auto text-sm text-muted-foreground">
                          {buyQty} credit{buyQty !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>

                    {/* Price summary */}
                    <div className="rounded-xl bg-muted/50 border border-border px-4 py-3 flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Total</span>
                      <span className="text-xl font-bold text-foreground">${(buyQty * 99).toLocaleString()}</span>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => setShowBuyModal(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        className="flex-1"
                        iconName="CreditCard"
                        iconPosition="left"
                        onClick={handleConfirmBuyCredits}
                        disabled={isLoadingPayment}
                      >
                        {isLoadingPayment ? 'Redirecting…' : 'Continue to Payment'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* ════════════ TRANSACTIONS ════════════ */}
              {activeTab === 'transactions' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-foreground">All Transactions</h3>
                    <button
                      onClick={loadAllTransactions}
                      className="text-xs text-primary hover:underline flex items-center gap-1"
                      disabled={loadingTransactions}
                    >
                      <Icon name="RefreshCw" size={12} className={loadingTransactions ? 'animate-spin' : ''} />
                      Refresh
                    </button>
                  </div>

                  <div className="bg-background border border-border rounded-xl overflow-hidden">
                    {loadingTransactions ? (
                      <div className="p-10 text-center">
                        <Icon name="Loader2" size={28} className="animate-spin mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">Loading transactions…</p>
                      </div>
                    ) : allTransactions.length === 0 ? (
                      <div className="p-10 text-center">
                        <Icon name="Receipt" size={44} className="mx-auto mb-3 text-muted-foreground" />
                        <p className="font-medium text-foreground mb-1">No transactions yet</p>
                        <p className="text-sm text-muted-foreground">Credit purchases, subscriptions and escrow payments will all appear here.</p>
                      </div>
                    ) : (
                      <>
                        {/* Table header */}
                        <div className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-4 px-5 py-3 bg-muted/40 border-b border-border text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                          <span className="w-8" />
                          <span>Description</span>
                          <span className="text-right">Amount</span>
                          <span className="w-24 text-center">Status</span>
                        </div>
                        <div className="divide-y divide-border">
                          {allTransactions.map((t) => (
                            <div key={t.id} className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-4 px-5 py-4 hover:bg-muted/30 transition-colors">
                              {/* Icon */}
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${t.iconBg}`}>
                                <Icon name={t.icon} size={16} className={t.iconColor} />
                              </div>

                              {/* Description */}
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">{t.description}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  <span className="bg-muted rounded px-1.5 py-0.5 mr-2">{t.category}</span>
                                  {new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </p>
                              </div>

                              {/* Amount */}
                              <p className={`text-sm font-bold tabular-nums whitespace-nowrap ${
                                t.flow === 'refund' ? 'text-success' :
                                t.status === 'voided' ? 'text-muted-foreground line-through' :
                                'text-foreground'
                              }`}>
                                {t.flow === 'refund' ? '+' : '−'}${(t.amount ?? 0).toFixed(2)}
                              </p>

                              {/* Status badge */}
                              <span className={`w-24 text-center text-xs px-2 py-0.5 rounded-full font-medium ${
                                ['succeeded', 'completed'].includes(t.status)        ? 'bg-success/10 text-success' :
                                ['held', 'pending', 'processing'].includes(t.status) ? 'bg-warning/10 text-warning' :
                                ['refunded'].includes(t.status)                      ? 'bg-success/10 text-success' :
                                ['voided'].includes(t.status)                        ? 'bg-muted text-muted-foreground line-through' :
                                ['failed', 'cancelled'].includes(t.status)           ? 'bg-destructive/10 text-destructive' :
                                'bg-muted text-muted-foreground'
                              }`}>
                                {t.status === 'held'     ? 'In Escrow'
                                  : t.status === 'succeeded' ? 'Paid'
                                  : t.status === 'voided'    ? 'Voided'
                                  : t.status === 'refunded'  ? 'Refunded'
                                  : t.status.charAt(0).toUpperCase() + t.status.slice(1)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* ════════════ PAYMENT METHOD ════════════ */}
              {activeTab === 'payment-method' && (
                <PaymentMethodTab userId={user?.id} />
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

// ── Payment method tab ────────────────────────────────────────────────────────
const PaymentMethodTab = ({ userId }) => {
  const [methods, setMethods] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    // If returning from Stripe card-save flow, sync first then load
    const params = new URLSearchParams(window.location.search);
    const justSetup = params.get('setup') === 'success';
    if (justSetup) {
      // Clean the URL without reloading
      const clean = window.location.pathname + '?tab=payment-method';
      window.history.replaceState({}, '', clean);
    }
    loadMethods(justSetup);
  }, [userId]);

  const loadMethods = async (syncFirst = false) => {
    try {
      setIsLoading(true);
      const { supabase } = await import('../../lib/supabase');

      // Sync from Stripe → DB whenever returning from card-save, or on first load
      await supabase.functions.invoke('sync-payment-methods', { body: { userId } });

      // Read from our DB
      const { data: fnData } = await supabase.functions.invoke(
        'get-payment-methods',
        { body: { userId } },
      );
      if (fnData?.methods) {
        setMethods(fnData.methods);
      }
    } catch {
      // show empty state
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCard = async () => {
    try {
      setIsAdding(true);
      const { supabase } = await import('../../lib/supabase');
      const { data, error } = await supabase.functions.invoke('create-billing-portal', {
        body: {
          userId,
          returnUrl: `${window.location.origin}/client-dashboard/financial?tab=payment-method`,
          setupMode: true,   // always use Stripe Checkout (auto-redirects on completion)
        },
      });
      if (error) {
        let msg = error.message;
        try {
          const body = await error.context?.json?.();
          if (body?.error) msg = body.error;
        } catch {}
        console.error('Card setup error detail:', msg);
        toast.error(msg || 'Failed to start payment setup');
        return;
      }
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not open payment setup');
      }
    } catch (err) {
      console.error('Add card error:', err);
      toast.error('Failed to start payment setup');
    } finally {
      setIsAdding(false);
    }
  };

  const handleSetDefault = async (methodId) => {
    try {
      const { supabase } = await import('../../lib/supabase');
      const { error } = await supabase.functions.invoke('set-default-payment-method', {
        body: { methodId, userId },
      });
      if (error) throw error;
      setMethods((prev) => prev.map((m) => ({ ...m, is_default: m.id === methodId })));
      toast.success('Default payment method updated');
    } catch {
      toast.error('Failed to update default payment method');
    }
  };

  const handleRemove = async (methodId) => {
    if (!window.confirm('Remove this payment method?')) return;
    try {
      const { supabase } = await import('../../lib/supabase');
      const { error } = await supabase.functions.invoke('remove-payment-method', {
        body: { methodId },
      });
      if (error) throw error;
      setMethods((prev) => prev.filter((m) => m.id !== methodId));
      toast.success('Payment method removed');
    } catch {
      toast.error('Failed to remove payment method');
    }
  };

  if (isLoading) {
    return (
      <div className="py-12 text-center">
        <Icon name="Loader2" size={28} className="animate-spin mx-auto text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-foreground">Saved Payment Methods</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Cards used for credit purchases and subscriptions.
          </p>
        </div>
        <Button
          variant="default"
          size="sm"
          iconName="Plus"
          iconPosition="left"
          onClick={handleAddCard}
          disabled={isAdding}
        >
          {isAdding ? 'Opening...' : 'Add Card'}
        </Button>
      </div>

      {methods.length === 0 ? (
        <div className="border border-dashed border-border rounded-xl py-14 text-center">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
            <Icon name="CreditCard" size={22} className="text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-foreground mb-1">No payment methods saved</p>
          <p className="text-xs text-muted-foreground mb-4">
            Add a card to enable quick credit purchases and subscription renewals.
          </p>
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={handleAddCard}
            disabled={isAdding}
          >
            Add Payment Method
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {methods.map((method) => (
            <div
              key={method.id}
              className="flex items-center justify-between p-4 bg-background border border-border rounded-xl"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                  <Icon name="CreditCard" size={18} className="text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground capitalize">
                    {method.card?.brand || 'Card'} ···· {method.card?.last4}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Expires {method.card?.exp_month}/{method.card?.exp_year}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {method.is_default ? (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary font-medium">
                    Default
                  </span>
                ) : (
                  <button
                    onClick={() => handleSetDefault(method.id)}
                    className="px-2 py-0.5 text-xs rounded-full border border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                    title="Make default"
                  >
                    Make Default
                  </button>
                )}
                <button
                  onClick={() => handleRemove(method.id)}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                  title="Remove"
                >
                  <Icon name="Trash2" size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Security notice */}
      <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-xl border border-border">
        <Icon name="ShieldCheck" size={16} className="text-success mt-0.5 flex-shrink-0" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          Payment details are securely stored and processed by{' '}
          <span className="font-medium text-foreground">Stripe</span>. We never store your full
          card number on our servers.
        </p>
      </div>
    </div>
  );
};

export default ClientFinancial;
